import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchBlogImageUrl } from "../../utils/apiUtils";
import { useAvatarSrc } from "../settings/useAvatarSrc";

const stripBlogHtml = (html) => String(html || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/\s+/g, " ")
    .trim();

const hasBlogBody = (html) => Boolean(
    stripBlogHtml(html)
    || /data-s3-key=|data-pending-id=|src=["']posts\//i.test(String(html || "")),
);

const useBlogMediaSrc = (key, kind = "image") => {
    const { data } = useQuery({
        queryKey: [`blog-${kind}`, key],
        queryFn: () => fetchBlogImageUrl(key),
        enabled: Boolean(key),
    });
    return data?.url || "";
};

const escapeBlogText = (text) => String(text || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const appendSpokenToBody = (html, spoken) => {
    const safe = escapeBlogText(spoken.trim());
    if (!safe) return html;
    if (!hasBlogBody(html)) return `<p>${safe}</p>`;
    if (/<\/p>\s*$/i.test(html)) {
        return html.replace(/<\/p>\s*$/i, ` ${safe}</p>`);
    }
    return `${html}<p>${safe}</p>`;
};

const displayName = (email) => {
    if (!email) return "Someone";
    const local = email.split("@")[0] || email;
    return local
        .split(/[._-]+/)
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(" ");
};

const initials = (email) => {
    const name = displayName(email);
    const parts = name.split(" ");
    if (parts.length > 1) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return name.slice(0, 1).toUpperCase();
};

const formatRelative = (iso) => {
    if (!iso) return "";
    const then = new Date(iso).getTime();
    const delta = Math.max(0, Date.now() - then);
    const minutes = Math.floor(delta / 60000);
    if (minutes < 1) return "just now";
    if (minutes < 60) return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;
    const months = Math.floor(days / 30);
    if (months < 12) return `${months} month${months === 1 ? "" : "s"} ago`;
    const years = Math.floor(days / 365);
    return years === 1 ? "a year ago" : `${years} years ago`;
};

const AvatarMark = ({ email }) => {
    const avatar = useAvatarSrc(email);
    const [failed, setFailed] = useState(false);
    useEffect(() => {
        setFailed(false);
    }, [avatar.src]);
    return (
        <span
            className="blog__avatar"
            aria-hidden="true"
            style={{ background: avatar.color }}
        >
            {avatar.src && !failed
                ? <img src={avatar.src} alt="" onError={() => setFailed(true)} />
                : initials(email)}
        </span>
    );
};

const Stars = ({ value = 0, onPick, disabled, readOnly }) => {
    const [hover, setHover] = useState(0);
    const rounded = Math.round(Number(value) || 0);
    const shown = hover || rounded;
    const interactive = Boolean(onPick) && !readOnly;

    const pick = (n) => (event) => {
        event.stopPropagation();
        if (!disabled && onPick) onPick(n);
    };

    return (
        <div
            className={`blog__stars${interactive ? " is-pick" : ""}`}
            role={interactive ? "radiogroup" : undefined}
            aria-label={`${rounded || 0} out of 5 stars`}
            onMouseLeave={() => setHover(0)}
        >
            {[1, 2, 3, 4, 5].map((n) => {
                const on = n <= shown;
                if (!interactive) {
                    return (
                        <span key={n} className={on ? "is-on" : ""}>★</span>
                    );
                }
                return (
                    <span
                        key={n}
                        role="radio"
                        tabIndex={disabled ? -1 : 0}
                        aria-checked={n === rounded}
                        aria-label={`${n} star${n === 1 ? "" : "s"}`}
                        className={on ? "is-on" : ""}
                        onClick={pick(n)}
                        onMouseEnter={() => setHover(n)}
                        onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") pick(n)(event);
                        }}
                    >
                        ★
                    </span>
                );
            })}
        </div>
    );
};

const BLOG_SEARCH_KEY = "blog-search-q";
const BLOG_SEARCH_FROM_DEFAULT = "2026-08-30";

const dayStamp = (value) => {
    const match = String(value || "").trim().match(/^(\d{4}-\d{2}-\d{2})/);
    return match ? match[1] : "";
};

const todayStamp = (now = new Date()) => {
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const stampToDate = (stamp) => {
    const day = dayStamp(stamp);
    if (!day) return null;
    const [year, month, dayNum] = day.split("-").map(Number);
    return new Date(year, month - 1, dayNum);
};

const parseBlogSearchRange = ({ from, to, now } = {}) => {
    const start = dayStamp(from) || BLOG_SEARCH_FROM_DEFAULT;
    let end = dayStamp(to) || todayStamp(now);
    if (end < start) end = start;
    return { from: start, to: end };
};

const readBlogSearch = () => {
    try {
        return sessionStorage.getItem(BLOG_SEARCH_KEY) || "";
    } catch {
        return "";
    }
};

const writeBlogSearch = (value) => {
    try {
        const q = String(value || "").trim();
        if (q) sessionStorage.setItem(BLOG_SEARCH_KEY, q);
        else sessionStorage.removeItem(BLOG_SEARCH_KEY);
    } catch {
        // ignore quota / private mode
    }
};

const blogHomePath = () => {
    const q = readBlogSearch();
    return q ? `/blog?q=${encodeURIComponent(q)}` : "/blog";
};

const ImageLightbox = ({ src, alt = "", onClose }) => {
    const closeRef = useRef(null);

    useEffect(() => {
        const onKey = (event) => {
            if (event.key === "Escape") onClose();
        };
        document.addEventListener("keydown", onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        closeRef.current?.focus();
        return () => {
            document.removeEventListener("keydown", onKey);
            document.body.style.overflow = prev;
        };
    }, [onClose]);

    return createPortal(
        <div
            className="blog__lightbox"
            role="dialog"
            aria-modal="true"
            aria-label="Full screen image"
            onClick={onClose}
        >
            <button
                ref={closeRef}
                type="button"
                className="blog__lightbox-close"
                onClick={onClose}
                aria-label="Close"
            >
                Close
            </button>
            <img src={src} alt={alt} onClick={(event) => event.stopPropagation()} />
        </div>,
        document.body,
    );
};

export {
    displayName,
    formatRelative,
    AvatarMark,
    Stars,
    readBlogSearch,
    writeBlogSearch,
    blogHomePath,
    BLOG_SEARCH_FROM_DEFAULT,
    parseBlogSearchRange,
    todayStamp,
    stampToDate,
    ImageLightbox,
    stripBlogHtml,
    hasBlogBody,
    appendSpokenToBody,
    useBlogMediaSrc,
};
